import { lazy, StrictMode, Suspense, useEffect } from "react";
import { useLocation } from "react-router-dom";
import "./App.css";
import { RequireModule } from "./components/access/RequireModule";
import { SentryErrorHandler } from "./components/SentryErrorHandler";
import { SnackbarProvider } from "./components/Snackbar";
import { TaxonomyProvider } from "./contexts/Taxonomy/TaxonomyContext";
import { Routes } from "./Routes";
import { clearSession, getUserClaims } from "./utils/auth";
import { trackPage } from "./utils/analytics";
import { LocalStorageProvider } from "./contexts/LocalStorage/LocalStorageProvider";
import { AppTopbar } from "./components/layout/AppTopbar";
import { BRAND_COLORS } from "./components/layout/constants";
import { AppSidebar } from "./components/layout/AppSidebar";
import { AppLayout } from "./components/layout/AppLayout";
import { AppTopbarUserMenu } from "./components/layout/AppTopbarUserMenu";
import { AppSwitcher } from "./components/layout/AppSwitcher";
import { isV2User } from "./utils/rbac";
import { SalesCopilotModal } from "./components/layout/SalesCopilotModal";
import { useNavigationConfig } from "./config/navigation-config";
import { AppLogo } from "./components/AppLogo";
import { ImpersonationBanner } from "./components/ImpersonationBanner";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import env from "./utils/env";
import {
  IMPERSONATION_BANNER_HEIGHT_PX,
  isImpersonating,
} from "./utils/impersonation";
import { useCommandPaletteShortcut } from "./components/CommandPalette/useCommandPaletteShortcut";
import { useFeatureFlag } from "./hooks/useLaunchDarkly";

const CommandPalette = lazy(() =>
  import("./components/CommandPalette/CommandPalette").then((module) => ({
    default: module.CommandPalette,
  }))
);

function App() {
  const location = useLocation();
  const navigationConfig = useNavigationConfig();
  const newSegmentsUi = useFeatureFlag("maNewSegmentsUi", false);

  useCommandPaletteShortcut();

  useEffect(() => {
    trackPage(location.pathname);
  }, [location.pathname]);

  /**
   * TEMP LOGOUT SOLUTION - STARTS
   * This is a temporal solution to complete the logout process
   */
  if (window.location.pathname === "/logout") {
    clearSession();
    return null;
  }

  const impersonating = isImpersonating();
  const bannerHeightPx = impersonating ? IMPERSONATION_BANNER_HEIGHT_PX : 0;

  return (
    <StrictMode>
      <LocalStorageProvider>
        <TaxonomyProvider>
          <SnackbarProvider>
            <SentryErrorHandler />
            <ImpersonationBanner />

            {/*
              RBAC v2 module gate (RGI-1042 / RGI-1113). Wraps the entire app
              shell so v2 users without `market_analyzer` see only the gate —
              no sidebar, topbar, or modals are reachable. v1 users (no
              `mkClaims.rbac` on JWT) and SuperKudu always pass through.
            */}
            <RequireModule
              moduleId="market_analyzer"
              moduleLabel="Market Analyzer"
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  height: `calc(100vh - ${bannerHeightPx}px)`,
                  marginTop: `${bannerHeightPx}px`,
                  width: "100vw",
                  overflow: "hidden",
                  alignItems: "flex-start",
                }}
              >
                <AppSidebar
                  navigationConfig={navigationConfig}
                  logo={<AppLogo />}
                  logoHref={env.ADMIN_URL || "/"}
                />
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    flex: 1,
                    minWidth: 0,
                    overflow: "hidden",
                    height: "100%",
                  }}
                >
                  <AppTopbar
                    leftContent={
                      isV2User(getUserClaims().rbac) ? (
                        <AppSwitcher />
                      ) : (
                        <Typography
                          variant="h6"
                          sx={{
                            fontSize: "18px",
                            fontWeight: 600,
                            color: BRAND_COLORS.PRUSSIAN,
                          }}
                        >
                          Market Analyzer
                        </Typography>
                      )
                    }
                    rightContent={
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        <AppTopbarUserMenu />
                      </Box>
                    }
                  />
                  <AppLayout
                    disablePadding={
                      newSegmentsUi &&
                      location.pathname.startsWith("/segments/")
                    }
                  >
                    <Routes />
                  </AppLayout>
                </Box>
                <SalesCopilotModal />
              </Box>
            </RequireModule>
            <Suspense fallback={null}>
              <CommandPalette />
            </Suspense>
          </SnackbarProvider>
        </TaxonomyProvider>
      </LocalStorageProvider>
    </StrictMode>
  );
}

export default App;
